import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { AlertCircle } from 'lucide-react';
import type { LandingPageEntry } from '@/components/glitch-ui/landing-page-combobox';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';
import { CompetitorCombobox } from '@/components/glitch-ui/competitor-combobox';
import type { AvailableCompetitor } from '@/lib/apis';

interface AdCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignUrl: string;
  setPrefixedUrl: (prefixedUrl: string) => void;
  handleCreateAd: () => Promise<void>;
  isLoading?: boolean;
  /** True for competitor campaigns: the ad targets a competitor, not a landing page. */
  isCompetitorCampaign?: boolean;
  competitors?: AvailableCompetitor[];
  isLoadingCompetitors?: boolean;
  setSelectedCompetitorId?: (competitorId: string | null) => void;
}

export const AdCreateModal = ({
  isOpen,
  onClose,
  campaignUrl,
  setPrefixedUrl,
  handleCreateAd,
  isCompetitorCampaign = false,
  competitors = [],
  isLoadingCompetitors = false,
  setSelectedCompetitorId,
}: AdCreateModalProps) => {
  const [selectedUrl, setSelectedUrl] = useState<string>(campaignUrl);
  const [selectedCompetitor, setSelectedCompetitor] = useState<AvailableCompetitor | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // Tell-on-click validation (#5857): the button stays enabled; clicking it
  // marks the missing URL instead of sitting silently disabled.
  const [urlError, setUrlError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      // Competitor ads always run against the campaign homepage, so the URL is fixed either way.
      setSelectedUrl(campaignUrl);
      setPrefixedUrl(campaignUrl);
      setUrlError(undefined);
      setSelectedCompetitor(null);
      setSelectedCompetitorId?.(null);
    }
  }, [isOpen]);

  const handleSelect = (entry: LandingPageEntry | null) => {
    const url = entry?.url ?? '';
    setSelectedUrl(url);
    setPrefixedUrl(url);
    setUrlError(undefined);
  };

  const handleCompetitorSelect = (competitor: AvailableCompetitor | null) => {
    setSelectedCompetitor(competitor);
    setSelectedCompetitorId?.(competitor?.id ?? null);
  };

  const handleCreate = async () => {
    if (!selectedUrl) {
      setUrlError('Required');
      return;
    }
    setIsCreating(true);
    try {
      await handleCreateAd();
      onClose();
    } catch {
      // Error handled by parent via toast — modal stays open
    } finally {
      setIsCreating(false);
    }
  };

  const noCompetitorsLeft = isCompetitorCampaign && !isLoadingCompetitors && competitors.length === 0;
  const canCreate = isCompetitorCampaign ? !!selectedCompetitor : !!selectedUrl;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='w-auto min-w-[min(92vw,420px)] max-w-[min(92vw,600px)]'>
        <DialogHeader>
          <DialogTitle>Add Ad</DialogTitle>
          <DialogDescription>
            {isCompetitorCampaign
              ? 'Choose the competitor this ad targets. Competitor ads run against the campaign homepage, so no landing page is needed.'
              : 'Choose a landing page for your new ad. It must be from the same domain as the campaign.'}
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          {isCompetitorCampaign ? (
            noCompetitorsLeft ? (
              <div className='border-l-4 border-[#FDE68A] bg-[#FEF3C7] text-[#A16207] p-3 text-sm flex items-start gap-2'>
                <AlertCircle className='h-4 w-4 flex-shrink-0 mt-0.5' />
                <span>
                  Every competitor linked to this campaign already has an ad. Add a competitor on the Competitors page
                  to create another.
                </span>
              </div>
            ) : (
              <CompetitorCombobox
                options={competitors}
                selectedId={selectedCompetitor?.id ?? null}
                onSelect={handleCompetitorSelect}
                isBusy={isLoadingCompetitors}
              />
            )
          ) : (
            <div>
              <div className='flex items-center justify-between'>
                <Label>Landing Page URL</Label>
                <FieldError message={urlError} />
              </div>
              <div className={urlError ? 'relative mt-1.5 rounded-md ring-1 ring-red-500' : 'relative mt-1.5'}>
                <BusinessLandingPageCombobox
                  selectedUrl={selectedUrl || null}
                  onSelect={handleSelect}
                  filterByDomain={campaignUrl}
                  placeholder="Select or paste a landing page URL..."
                />
                <InputAdornment error={!!urlError} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <div className='flex flex-row justify-end space-x-2'>
            <Button variant='outline' onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating || (isCompetitorCampaign && !canCreate)}
              className='bg-[#290BD9] hover:bg-[#2309C5] text-white'
            >
              {isCreating ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Ad'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
